// eslint:disable:no-unused-expression
import * as React from 'react';
import debounce from 'lodash/debounce';

export interface DebounceInputProps {
  id?: string;
  type?: string;
  className?: string;
  style?: any;
  value: string;
  placeholder: string;
  readOnly?: boolean;
  maxLength?: number;
  required?: boolean;
  focus?: boolean;
  onChange: (value: string) => void;
  onFocus?: (e: React.FormEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FormEvent<HTMLInputElement>) => void;
  /**
   * The number of milliseconds to delay onChange event. Default is 500.
   */
  wait?: number;
}

export interface DebounceInputState {
  inputValue?: string;
  previousValue?: string;
  hasFocus?: boolean;
}

/**
 * Prevent react warnings about uncontrolled components when value is undefined
 *
 * @param input
 */
const controlledValue = (input: any) =>  input === undefined ? '' : input;

/**
 *
 * Component behaves like HTML input tag with delayed onChange function.
 *
 */
export class DebounceInput extends React.Component<DebounceInputProps, DebounceInputState> {
  public static getDerivedStateFromProps(props:DebounceInputProps, state:DebounceInputState) {
    if (!state.hasFocus || (state.inputValue === '' && state.previousValue !== props.value)) {
      return {
        inputValue: controlledValue(props.value),
        previousValue: props.value
      };
    }
    return {previousValue: props.value};
  }

  public static defaultProps: Partial<DebounceInputProps> = {
    wait: 500,
    type: 'text'
  };
  private debounceOnChange: any;

  constructor(props: DebounceInputProps) {
    super(props);

    this.state = {
      inputValue: controlledValue(props.value),
      previousValue: props.value,
      hasFocus: false
    };

    this.debounceOnChange = debounce((value: string) => {
      this.props.onChange(value);
    }, this.props.wait);
  }

  private onChange = (e: React.FormEvent<HTMLInputElement>) => {
    const eventClone = Object.assign({}, e);
    this.setState({
      inputValue: controlledValue(eventClone.currentTarget.value)
    }, () => {
      this.debounceOnChange(eventClone.currentTarget.value);
    });
  };

  private onFocus = (e: React.FormEvent<HTMLInputElement>) => {
    this.setState({hasFocus: true});
    this.props.onFocus && this.props.onFocus(e);
  };

  private onBlur = (e: React.FormEvent<HTMLInputElement>) => {
    this.setState({hasFocus: false});
    this.props.onBlur && this.props.onBlur(e);
    if (this.props.value !== this.state.inputValue) {
      this.props.onChange && this.props.onChange(this.state.inputValue);
    }
  };

  public render() {
    return (<input
      type={this.props.type}
      autoFocus={this.props.focus}
      className={this.props.className}
      style={this.props.style}
      value={this.state.inputValue}
      readOnly={this.props.readOnly}
      disabled={this.props.readOnly}
      maxLength={this.props.maxLength}
      placeholder={this.props.placeholder}
      required={this.props.required}
      onChange={this.onChange}
      onFocus={this.onFocus}
      onBlur={this.onBlur}
    />);
  }
}
